import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Slider from 'react-slick';
import { FaCamera, FaPlay, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { creativityGallary, festivalGallary } from '../constant/index';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const PhotoGallery = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('creativity');
  const [showAllPhotos, setShowAllPhotos] = useState(false);

  const allImages = [...creativityGallary, ...festivalGallary];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('gallery-section');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const creativitySettings = {
    infinite: true,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    speed: 800,
    cssEase: 'cubic-bezier(0.4, 0, 0.2, 1)',
    variableWidth: false,
    arrows: false,
    dots: false,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
        }
      }
    ]
  };

  const festivalSettings = {
    infinite: true,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3500,
    speed: 800,
    cssEase: 'cubic-bezier(0.4, 0, 0.2, 1)',
    variableWidth: false,
    rtl: true,
    arrows: false,
    dots: false,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
        }
      }
    ]
  };

  const openLightbox = (image, index, gallery) => {
    setSelectedImage(image);
    let galleryImages;
    if (gallery === 'creativity') {
      galleryImages = creativityGallary;
    } else if (gallery === 'festival') {
      galleryImages = festivalGallary;
    } else {
      galleryImages = allImages;
    }
    setCurrentIndex(galleryImages.findIndex(img => img.id === image.id));
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const navigateImage = (direction) => {
    let currentGallery;
    if (showAllPhotos) {
      currentGallery = allImages;
    } else {
      currentGallery = activeTab === 'creativity' ? creativityGallary : festivalGallary;
    }

    let newIndex;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % currentGallery.length;
    } else {
      newIndex = currentIndex === 0 ? currentGallery.length - 1 : currentIndex - 1;
    }

    setCurrentIndex(newIndex);
    setSelectedImage(currentGallery[newIndex]);
  };

  const handleViewAllPhotos = () => {
    setShowAllPhotos(true);
  };

  const handleBackToSlider = () => {
    setShowAllPhotos(false);
  };

  return (
    <section id="gallery-section" className="py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary-100/20 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-secondary-100/20 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2"></div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="inline-flex items-center space-x-2 bg-accent-100 text-accent-700 px-6 py-2 rounded-full text-sm font-semibold mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isVisible ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <FaCamera />
            <span>Our Memories</span>
          </motion.div>

          <h2 className="text-4xl lg:text-6xl font-bold font-platypi text-gray-800 mb-6">
            <span className="gradient-text bg-gradient-to-r from-accent-600 to-primary-600 bg-clip-text text-transparent">
              Photo
            </span>
            <br />
            <span className="text-gray-700">Gallery</span>
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Capturing precious moments of learning, growth, and joy in our educational journey.
          </p>
        </motion.div>

        {/* Tab Navigation */}
        {!showAllPhotos && (
          <motion.div
            className="flex justify-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
          <div className="bg-white rounded-2xl p-2 shadow-soft">
            <button
              onClick={() => setActiveTab('creativity')}
              className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                activeTab === 'creativity'
                  ? 'bg-gradient-primary text-white shadow-medium'
                  : 'text-gray-600 hover:text-primary-600'
              }`}
            >
              Learning Activities
            </button>
            <button
              onClick={() => setActiveTab('festival')}
              className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                activeTab === 'festival'
                  ? 'bg-gradient-secondary text-white shadow-medium'
                  : 'text-gray-600 hover:text-secondary-600'
              }`}
            >
              Special Events
            </button>
          </div>
        </motion.div>
        )}

        {/* Gallery Sliders */}
        {!showAllPhotos && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
          {/* Creativity Gallery */}
          <div className={`mb-12 ${activeTab !== 'creativity' ? 'hidden' : ''}`}>
            <Slider {...creativitySettings}>
              {creativityGallary.map((item, index) => (
                <div key={item.id} className="px-3">
                  <motion.div
                    className="relative group cursor-pointer"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                    onClick={() => openLightbox(item, index, 'creativity')}
                  >
                    <div className="relative overflow-hidden rounded-2xl shadow-medium group-hover:shadow-strong transition-all duration-300">
                      <img
                        src={item.image}
                        alt={`Learning activity ${item.id}`}
                        className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                      />

                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="flex items-center justify-between text-white">
                            <span className="text-sm font-medium">Learning Activity</span>
                            <FaPlay className="text-lg" />
                          </div>
                        </div>
                      </div>

                      {/* Hover Effect */}
                      <div className="absolute inset-0 bg-primary-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                  </motion.div>
                </div>
              ))}
            </Slider>
          </div>

          {/* Festival Gallery */}
          <div className={`${activeTab !== 'festival' ? 'hidden' : ''}`}>
            <Slider {...festivalSettings}>
              {festivalGallary.map((item, index) => (
                <div key={item.id} className="px-3">
                  <motion.div
                    className="relative group cursor-pointer"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                    onClick={() => openLightbox(item, index, 'festival')}
                  >
                    <div className="relative overflow-hidden rounded-2xl shadow-medium group-hover:shadow-strong transition-all duration-300">
                      <img
                        src={item.image}
                        alt={`Special event ${item.id}`}
                        className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                      />

                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="flex items-center justify-between text-white">
                            <span className="text-sm font-medium">Special Event</span>
                            <FaPlay className="text-lg" />
                          </div>
                        </div>
                      </div>

                      {/* Hover Effect */}
                      <div className="absolute inset-0 bg-secondary-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                  </motion.div>
                </div>
              ))}
            </Slider>
          </div>
        </motion.div>
        )}

        {/* View All Button */}
        {!showAllPhotos && (
          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <button
              onClick={handleViewAllPhotos}
              className="bg-gradient-accent text-white px-8 py-4 rounded-2xl font-semibold text-lg shadow-medium hover:shadow-strong transition-all duration-300 group"
            >
              <span className="flex items-center space-x-2">
                <FaCamera className="group-hover:animate-bounce" />
                <span>View All Photos</span>
              </span>
            </button>
          </motion.div>
        )}

        {/* All Photos Grid View */}
        {showAllPhotos && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-12"
          >
            {/* Back Button */}
            <div className="flex justify-between items-center mb-8">
              <button
                onClick={handleBackToSlider}
                className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold transition-all duration-300"
              >
                <FaChevronLeft />
                <span>Back to Slider</span>
              </button>
              <div className="text-gray-600 font-medium">
                Showing all {allImages.length} photos
              </div>
            </div>

            {/* All Photos Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {allImages.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="relative group cursor-pointer"
                  onClick={() => openLightbox(item, index, 'all')}
                >
                  <div className="relative overflow-hidden rounded-xl shadow-medium group-hover:shadow-strong transition-all duration-300">
                    <img
                      src={item.image}
                      alt={`Photo ${item.id}`}
                      className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                    />

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-2 left-2 right-2">
                        <div className="flex items-center justify-between text-white">
                          <span className="text-xs font-medium">
                            {creativityGallary.includes(item) ? 'Learning' : 'Event'}
                          </span>
                          <FaPlay className="text-sm" />
                        </div>
                      </div>
                    </div>

                    {/* Hover Effect */}
                    <div className="absolute inset-0 bg-primary-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
          >
            <motion.div
              className="relative max-w-4xl max-h-full"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={closeLightbox}
                className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors z-10"
              >
                <FaTimes size={24} />
              </button>

              {/* Navigation Buttons */}
              <button
                onClick={() => navigateImage('prev')}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-3"
              >
                <FaChevronLeft size={20} />
              </button>

              <button
                onClick={() => navigateImage('next')}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-3"
              >
                <FaChevronRight size={20} />
              </button>

              {/* Image */}
              <img
                src={selectedImage.image}
                alt={`Gallery image ${selectedImage.id}`}
                className="w-full h-full object-contain rounded-lg"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default PhotoGallery;